import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import socketService from '../../utils/socketService';
import {
  FaWallet,
  FaArrowLeft,
  FaArrowUp,
  FaArrowDown,
  FaFilter,
  FaCalendarAlt,
  FaRupeeSign,
  FaReceipt,
  FaEye,
  FaDownload,
  FaSearch,
  FaImage,
  FaChevronDown,
  FaChevronUp
} from 'react-icons/fa';
import DriverLayout from '../../layouts/DriverLayout';
import {
  getDriverWallet,
  getDriverTransactions,
  reset as resetWallet
} from '../../features/wallet/walletSlice';

const WalletHistory = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { driver: driverAuth } = useSelector((state) => state.driverAuth);
  const { wallet, transactions, isLoading, isError, message } = useSelector((state) => state.wallet);

  const [filters, setFilters] = useState({
    type: 'all',
    dateRange: '30',
    search: ''
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [expandedGroups, setExpandedGroups] = useState({});

  const toggleGroup = (bookingNumber) => {
    setExpandedGroups(prev => ({
      ...prev,
      [bookingNumber]: !prev[bookingNumber]
    }));
  };

  // Get driver ID
  const driverId = driverAuth?._id || driverAuth?.driver?._id;

  useEffect(() => {
    if (driverId) {
      // Always fetch fresh wallet data to ensure current balance is up-to-date
      dispatch(getDriverWallet(driverId));
      dispatch(getDriverTransactions({
        driverId,
        limit: 10000
      }));
    }
  }, [dispatch, driverId]);

  useEffect(() => {
    if (isError && message) {
      toast.error(message);
      dispatch(resetWallet());
    }
  }, [isError, message, dispatch]);

  // Listen for wallet and commission updates via socket events
  useEffect(() => {
    if (!driverId) return;

    const socket = socketService.connect();
    if (!socket) return;

    // Handle ride completion - refresh wallet data immediately
    const handleRideCompleted = (data) => {
      console.log('📡 Ride completed event received:', data);
      if (data.driverId === driverId || data.driver === driverId) {
        console.log('🔄 Refreshing wallet data after ride completion...');
        dispatch(getDriverWallet(driverId));
        dispatch(getDriverTransactions({
          driverId,
          limit: 10000
        }));
      }
    };

    // Handle wallet updates - refresh wallet data immediately
    const handleWalletUpdated = (data) => {
      console.log('📡 Wallet updated event received:', data);
      if (data.driverId === driverId || data.driver === driverId) {
        console.log('🔄 Refreshing wallet data after wallet update...');
        dispatch(getDriverWallet(driverId));
      }
    };

    // Handle commission processed - refresh wallet data immediately
    const handleCommissionProcessed = (data) => {
      console.log('📡 Commission processed event received:', data);
      if (data.driverId === driverId || data.driver === driverId) {
        console.log('🔄 Refreshing wallet data after commission processing...');
        dispatch(getDriverWallet(driverId));
        dispatch(getDriverTransactions({
          driverId,
          limit: 10000
        }));
      }
    };

    // Register socket event listeners
    socket.on(socketService.events.RIDE_COMPLETED, handleRideCompleted);
    socket.on(socketService.events.WALLET_UPDATED, handleWalletUpdated);
    socket.on(socketService.events.COMMISSION_PROCESSED, handleCommissionProcessed);

    console.log('📡 Socket event listeners registered for wallet updates');

    // Cleanup listeners on unmount
    return () => {
      socket.off(socketService.events.RIDE_COMPLETED, handleRideCompleted);
      socket.off(socketService.events.WALLET_UPDATED, handleWalletUpdated);
      socket.off(socketService.events.COMMISSION_PROCESSED, handleCommissionProcessed);
      console.log('📡 Socket event listeners removed');
    };
  }, [driverId, dispatch]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'ride_earnings':
      case 'ride_earning': // Backend uses singular form
      case 'earning':
      case 'online_ride_earning':
      case 'online_ride_earning_base':
      case 'extra_km_earning':
      case 'waiting_charge_earning':
      case 'advance_paid':
      case 'remaining_ride_earning':
      case 'grouped_ride':
      case 'cod_cash_collection':
        return <FaArrowUp className="text-green-500" />;
      case 'commission_deduction':
      case 'admin_commission_deduction':
      case 'vendor_commission_deduction':
      case 'gst_deduction':
      case 'commission_payment':
      case 'commission_cut':
      case 'cod_ride_deduction':
      case 'cod_commission_deduction':
      case 'cod_admin_commission_deduction':
      case 'cod_vendor_commission_deduction':
      case 'cod_gst_deduction':
      case 'cod_commission_settlement':
        return <FaArrowDown className="text-red-500" />;
      case 'withdrawal':
      case 'withdrawal_approved':
        return <FaArrowUp className="text-blue-500" />;
      case 'deposit':
      case 'wallet_topup':
        return <FaArrowDown className="text-blue-500" />;
      default:
        return <FaReceipt className="text-gray-500" />;
    }
  };

  const getTransactionColor = (type) => {
    switch (type) {
      case 'ride_earnings':
      case 'ride_earning': // Backend uses singular form
      case 'earning':
      case 'online_ride_earning':
      case 'online_ride_earning_base':
      case 'extra_km_earning':
      case 'waiting_charge_earning':
      case 'advance_paid':
      case 'remaining_ride_earning':
      case 'grouped_ride':
      case 'cod_cash_collection':
      case 'deposit':
      case 'wallet_topup':
        return 'text-green-600';
      case 'commission_deduction':
      case 'admin_commission_deduction':
      case 'vendor_commission_deduction':
      case 'gst_deduction':
      case 'commission_payment':
      case 'commission_cut':
      case 'cod_ride_deduction':
      case 'cod_commission_deduction':
      case 'cod_admin_commission_deduction':
      case 'cod_vendor_commission_deduction':
      case 'cod_gst_deduction':
      case 'cod_commission_settlement':
      case 'withdrawal':
      case 'withdrawal_approved':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getTransactionTitle = (type, tx = {}) => {
    if (tx.isCombinedCommission) {
      return 'Commission Deducted';
    }
    // For cod_ types that actually belong to an online booking (extra charges), show correct label
    const isExtraChargeCOD = (type === 'cod_admin_commission_deduction' || type === 'cod_vendor_commission_deduction' || type === 'cod_gst_deduction')
      && tx.paymentMethod === 'online';
    switch (type) {
      case 'ride_earnings':
      case 'ride_earning': // Backend uses singular form
      case 'earning':
        return 'Ride Earnings';
      case 'online_ride_earning_base':
        return 'Ride Base Fare';
      case 'extra_km_earning':
        return 'Extra Km Earning';
      case 'waiting_charge_earning':
        return 'Waiting Charge Earning';
      case 'advance_paid':
        return 'Advance Payment Credit';
      case 'remaining_ride_earning':
        return 'Remaining Ride Credit';
      case 'online_ride_earning': {
        const desc = (tx && tx.description) ? tx.description.toLowerCase() : '';
        if (desc.includes('remaining')) {
          return 'Remaining Ride Credit';
        }
        if (desc.includes('upfront') || desc.includes('advance')) {
          return 'Advance Payment Credit';
        }
        const method = (tx.bookingPaymentMethod || tx.paymentMethod || '').toUpperCase();
        if (method === 'WALLET') {
          return 'Wallet Ride';
        }
        return 'Online Ride';
      }
      case 'cod_cash_collection':
        return 'Remaining amount (COD)';
      case 'grouped_ride': {
        const method = (tx.paymentMethod || '').toUpperCase();
        if (method === 'COD') {
          return 'COD Ride';
        }
        if (method === 'WALLET') {
          return 'Wallet Ride';
        }
        return 'Online Ride';
      }
      case 'commission_deduction':
        return 'Ride Commission';
      case 'admin_commission_deduction':
        return 'Commission Deducted';
      case 'vendor_commission_deduction':
        return 'Commission Deducted';
      case 'gst_deduction':
        return 'GST Tax';
      case 'commission_payment':
        return 'Commission Payment';
      case 'commission_cut':
        return 'Commission Deducted';
      case 'cod_ride_deduction':
        return 'COD Ride Deduction';
      case 'cod_commission_deduction':
        return 'Ride Commission';
      case 'cod_admin_commission_deduction':
        return isExtraChargeCOD ? 'Extra Charges Commission' : 'Commission Deducted';
      case 'cod_vendor_commission_deduction':
        return isExtraChargeCOD ? 'Extra Charges Commission' : 'Commission Deducted';
      case 'cod_gst_deduction':
        return isExtraChargeCOD ? 'Extra Charges GST' : 'GST Tax';
      case 'cod_commission_settlement':
        return 'Commission Settlement';
      case 'withdrawal':
      case 'withdrawal_approved':
        return 'Withdrawal';
      case 'deposit':
      case 'wallet_topup':
        return 'Wallet Top-up';
      default:
        return 'Transaction';
    }
  };

  // Helper function to categorize transaction types
  const getTransactionCategory = (type) => {
    if (['ride_earnings', 'ride_earning', 'earning', 'online_ride_earning', 'online_ride_earning_base', 'extra_km_earning', 'waiting_charge_earning', 'advance_paid', 'remaining_ride_earning', 'grouped_ride', 'cod_cash_collection'].includes(type)) {
      return 'earnings';
    }
    if (['commission_deduction', 'admin_commission_deduction', 'vendor_commission_deduction',
         'gst_deduction', 'commission_payment', 'commission_cut', 'cod_commission_deduction',
         'cod_admin_commission_deduction', 'cod_vendor_commission_deduction', 'cod_gst_deduction',
         'cod_commission_settlement'].includes(type)) {
      return 'commission';
    }
    if (['withdrawal', 'withdrawal_approved'].includes(type)) {
      return 'withdrawal';
    }
    if (['deposit', 'wallet_topup'].includes(type)) {
      return 'deposit';
    }
    return 'other';
  };

  // Use server-side transactions if available, otherwise fall back to wallet transactions
  const transactionsToUse = transactions && transactions.length > 0 ? transactions : (wallet?.transactions || []);

  // Prefer custom booking number if provided by API
  const getDisplayBookingId = (tx) => {
    const candidate = tx.bookingNumber || tx.customBookingId || tx.bookingId;
    if (!candidate) return '';
    if (typeof candidate === 'object' && candidate !== null) {
      return candidate.bookingId || candidate.bookingNumber || candidate.customBookingId || candidate._id || '';
    }
    return String(candidate);
  };

  // Replace ObjectId in description with custom booking number if available
  const formatTransactionDescription = (tx) => {
    const original = tx.description || 'No description';
    const rawId = tx.bookingId || '';
    const bookingNumber = tx.bookingNumber || tx.customBookingId || '';

    // Special handling for settlement entries to avoid mislabeling them as COD commission for a booking
    if (tx.type === 'cod_commission_settlement') {
      return 'Commission settlement from previous COD rides';
    }

    if (tx.isCombinedCommission) {
      const displayId = getDisplayBookingId(tx);
      if (displayId) return `Commission deducted for booking #${displayId}`;
      return 'Commission deducted for booking';
    }

    const commissionTypes = new Set([
      'commission_deduction',
      'admin_commission_deduction',
      'vendor_commission_deduction',
      'gst_deduction',
      'commission_payment',
      'commission_cut',
      'cod_commission_deduction',
      'cod_admin_commission_deduction',
      'cod_vendor_commission_deduction',
      'cod_gst_deduction',
    ]);

    if (commissionTypes.has(tx.type)) {
      // Check if this is a cod_ transaction but the paymentMethod is 'online'
      // This happens when extra charges (extraKm/waiting) are collected as COD for an online booking
      const isCODType = tx.type.startsWith('cod_');
      const isExtraChargeForOnlineBooking = isCODType && tx.paymentMethod === 'online';
      const displayId = getDisplayBookingId(tx);

      if (isExtraChargeForOnlineBooking) {
        // These are extra charges (extraKm/waitingCharge) collected as COD on an online booking
        const isGST = tx.type === 'cod_gst_deduction';
        if (displayId) {
          return isGST
            ? `GST on extra charges for online booking #${displayId}`
            : `Commission on extra charges for online booking #${displayId}`;
        }
        return isGST
          ? 'GST on extra charges for online booking'
          : 'Commission on extra charges for online booking';
      }

      // For admin_commission_deduction (online/wallet ride)
      if (tx.type === 'admin_commission_deduction' || tx.type === 'vendor_commission_deduction') {
        const method = (tx.bookingPaymentMethod || tx.paymentMethod || '').toUpperCase();
        const methodLabel = method === 'WALLET' ? 'wallet' : 'online';
        if (displayId) return `Commission deducted for ${methodLabel} booking #${displayId}`;
        return `Commission deducted for ${methodLabel} booking`;
      }

      // For gst_deduction
      if (tx.type === 'gst_deduction') {
        const method = (tx.bookingPaymentMethod || tx.paymentMethod || '').toUpperCase();
        const methodLabel = method === 'WALLET' ? 'wallet' : 'online';
        if (displayId) return `GST deducted for ${methodLabel} booking #${displayId}`;
        return `GST deducted for ${methodLabel} booking`;
      }

      // For COD commission/GST deductions (pure COD bookings or partial-payment + COD remaining)
      if (tx.type === 'cod_admin_commission_deduction' || tx.type === 'cod_vendor_commission_deduction') {
        if (displayId) return `Commission deducted for booking #${displayId}`;
        return 'Commission deducted for booking';
      }

      if (tx.type === 'cod_gst_deduction') {
        if (displayId) return `GST deducted for booking #${displayId}`;
        return 'GST deducted for booking';
      }

      // Standard label based on type prefix
      const flavor = isCODType ? 'COD' : (tx.bookingPaymentMethod || tx.paymentMethod || '').toLowerCase() === 'wallet' ? 'wallet' : 'online';
      if (displayId) {
        return `Ride commission for ${flavor} booking #${displayId}`;
      }
      return `Ride commission for ${flavor} booking`;
    }


    if (!bookingNumber) return original;

    let updated = original;
    if (rawId && typeof rawId === 'string') {
      updated = updated.replace(rawId, bookingNumber);
    }
    updated = updated.replace(/(booking\s*#)[a-fA-F0-9]{24}/i, `$1${bookingNumber}`);
    return updated;
  };

  // Group all transactions belonging to the same booking into a single 'grouped_ride' transaction
  const combinedTransactions = (() => {
    // If the API already returned pre-grouped transactions, return them directly after combining their commissions
    const alreadyGrouped = transactionsToUse.some(t => t.type === 'grouped_ride');
    if (alreadyGrouped) {
      return transactionsToUse.map(group => {
        if (group.type !== 'grouped_ride') return group;

        const processedTxs = [];
        let combinedCommission = null;

        const isVendor = group.driverType === 'vendor';
        const COMMISSION_TYPES = isVendor ? new Set([
          'admin_commission_deduction',
          'vendor_commission_deduction',
          'cod_admin_commission_deduction',
          'cod_vendor_commission_deduction',
        ]) : new Set();

        for (const t of group.transactions) {
          if (COMMISSION_TYPES.has(t.type)) {
            const isCOD = t.type.startsWith('cod_');
            if (!combinedCommission) {
              combinedCommission = {
                ...t,
                type: isCOD ? 'cod_commission_deduction' : 'commission_deduction',
                isCombinedCommission: true,
                adminCommissionAmount: t.type.includes('admin') ? (t.amount || 0) : (t.adminCommissionAmount || 0) || 0,
                vendorCommissionAmount: t.type.includes('vendor') ? (t.amount || 0) : (t.vendorCommissionAmount || 0) || 0,
              };
            } else {
              combinedCommission.amount = (combinedCommission.amount || 0) + (t.amount || 0);
              if (t.type.includes('admin')) {
                combinedCommission.adminCommissionAmount = (combinedCommission.adminCommissionAmount || 0) + (t.amount || 0);
              }
              if (t.type.includes('vendor')) {
                combinedCommission.vendorCommissionAmount = (combinedCommission.vendorCommissionAmount || 0) + (t.amount || 0);
              }
              if (new Date(t.date) > new Date(combinedCommission.date)) {
                combinedCommission.date = t.date;
              }
            }
          } else {
            const isEarning = t.type === 'online_ride_earning' || t.type === 'ride_earning' || t.type === 'earning';
            if (isEarning) {
              const isExtraCOD = t.bookingDetails?.extraChargesCollectedAsCOD === true;
              const txAmount = Math.abs(t.amount || 0);
              const bookingTotalAmount = t.bookingDetails?.totalAmount || 0;
              const shouldSubtract = bookingTotalAmount > 0 && txAmount > bookingTotalAmount;

              const extraKm = (isExtraCOD || !shouldSubtract) ? 0 : (t.extraKmCharge || t.bookingDetails?.extraKmCharge || 0);
              const waiting = (isExtraCOD || !shouldSubtract) ? 0 : (t.waitingCharge || t.bookingDetails?.waitingCharge || 0);
              const baseFare = Math.max(0, txAmount - extraKm - waiting);

              // Always push base fare as online_ride_earning_base for consistent display title/desc
              processedTxs.push({
                ...t,
                type: 'online_ride_earning_base',
                amount: baseFare,
                description: "Ride base fare credited",
              });

              // 2. Extra Km earning row
              if (extraKm > 0) {
                processedTxs.push({
                  ...t,
                  type: 'extra_km_earning',
                  amount: extraKm,
                  description: "Extra distance charges credited",
                });
              }

              // 3. Waiting Charge earning row
              if (waiting > 0) {
                processedTxs.push({
                  ...t,
                  type: 'waiting_charge_earning',
                  amount: waiting,
                  description: "Waiting time charges credited",
                });
              }
            } else {
              processedTxs.push(t);
            }
          }
        }

        if (combinedCommission) {
          processedTxs.push(combinedCommission);
        }

        const getPriority = (tx) => {
          if (tx.type === 'online_ride_earning' || tx.type === 'ride_earning' || tx.type === 'earning' || tx.type === 'online_ride_earning_base') {
            const isAdvance = tx.description && (tx.description.toLowerCase().includes('upfront') || tx.description.toLowerCase().includes('advance'));
            return isAdvance ? 1 : 2;
          }
          if (tx.type === 'extra_km_earning') return 2;
          if (tx.type === 'waiting_charge_earning') return 2;
          if (tx.type === 'cod_cash_collection') return 2;
          if (tx.type === 'commission_deduction' || tx.type === 'cod_commission_deduction') return 3;
          if (tx.type === 'gst_deduction' || tx.type === 'cod_gst_deduction') return 4;
          return 5;
        };

        processedTxs.sort((a, b) => getPriority(a) - getPriority(b));

        return {
          ...group,
          transactions: processedTxs
        };
      });
    }

    const preSplitTxs = [];
    for (const tx of transactionsToUse) {
      const isEarning = tx.type === 'online_ride_earning' || tx.type === 'ride_earning' || tx.type === 'earning';
      if (isEarning) {
        const isExtraCOD = tx.bookingDetails?.extraChargesCollectedAsCOD === true;
        const txAmount = Math.abs(tx.amount || 0);
        const bookingTotalAmount = tx.bookingDetails?.totalAmount || 0;
        const shouldSubtract = bookingTotalAmount > 0 && txAmount > bookingTotalAmount;

        const extraKm = (isExtraCOD || !shouldSubtract) ? 0 : (tx.extraKmCharge || tx.bookingDetails?.extraKmCharge || 0);
        const waiting = (isExtraCOD || !shouldSubtract) ? 0 : (tx.waitingCharge || tx.bookingDetails?.waitingCharge || 0);
        const baseFare = Math.max(0, txAmount - extraKm - waiting);

        // Always push base fare as online_ride_earning_base for consistent display title/desc
        preSplitTxs.push({
          ...tx,
          type: 'online_ride_earning_base',
          amount: baseFare,
          description: "Ride base fare credited",
        });

        // 2. Extra Km earning row
        if (extraKm > 0) {
          preSplitTxs.push({
            ...tx,
            type: 'extra_km_earning',
            amount: extraKm,
            description: `Extra distance charges credited for booking #${getDisplayBookingId(tx)}`,
          });
        }

        // 3. Waiting Charge earning row
        if (waiting > 0) {
          preSplitTxs.push({
            ...tx,
            type: 'waiting_charge_earning',
            amount: waiting,
            description: "Waiting time charges credited",
          });
        }
      } else {
        preSplitTxs.push(tx);
      }
    }

    const bookingGroups = new Map();
    const nonBookingTransactions = [];

    for (const tx of preSplitTxs) {
      // Do not group extra_km_earning transactions under the main booking ride group
      const bookingKey = tx.type === 'extra_km_earning' ? null : getDisplayBookingId(tx);

      if (bookingKey) {
        if (!bookingGroups.has(bookingKey)) {
          bookingGroups.set(bookingKey, {
            type: 'grouped_ride',
            bookingId: tx.bookingId,
            bookingNumber: tx.bookingNumber || tx.customBookingId || bookingKey,
            paymentMethod: tx.paymentMethod || 'online',
            totalFare: tx.totalFare || 0,
            driverType: tx.driverType || 'admin',
            date: tx.date,
            transactions: [],
          });
        }
        const group = bookingGroups.get(bookingKey);
        group.transactions.push(tx);

        // Track the highest totalFare
        if (tx.totalFare && tx.totalFare > group.totalFare) {
          group.totalFare = tx.totalFare;
        }
        // Track the latest date
        if (new Date(tx.date) > new Date(group.date)) {
          group.date = tx.date;
        }
        // Preserve paymentMethod and driverType if they are set
        if (tx.paymentMethod && group.paymentMethod === 'online') {
          group.paymentMethod = tx.paymentMethod;
        }
        if (tx.driverType && group.driverType === 'admin') {
          group.driverType = tx.driverType;
        }
      } else {
        nonBookingTransactions.push(tx);
      }
    }

    // Convert map to list, combine commissions inside each group, and sort the child transactions
    const groupedList = Array.from(bookingGroups.values()).map(group => {
      // Calculate/normalize totalFare first so it is available for remainingAmount calculation
      if (!group.totalFare) {
        const earningTxs = group.transactions.filter(t => t.type.includes('earning'));
        if (earningTxs.length > 0) {
          group.totalFare = earningTxs.reduce((sum, t) => sum + Math.abs(t.amount || 0), 0);
        } else {
          const txWithFare = group.transactions.find(t => t.totalFare);
          if (txWithFare) {
            group.totalFare = txWithFare.totalFare;
          } else {
            group.totalFare = Math.max(...group.transactions.map(t => Math.abs(t.amount || 0)));
          }
        }
      }

      // Post-process the group's child transactions to combine admin/vendor commissions
      const processedTxs = [];
      let combinedCommission = null;

      const isVendor = group.driverType === 'vendor';
      const COMMISSION_TYPES = isVendor ? new Set([
        'admin_commission_deduction',
        'vendor_commission_deduction',
        'cod_admin_commission_deduction',
        'cod_vendor_commission_deduction',
      ]) : new Set();

      for (const t of group.transactions) {
        if (COMMISSION_TYPES.has(t.type)) {
          const isCOD = t.type.startsWith('cod_');
          if (!combinedCommission) {
            combinedCommission = {
              ...t,
              type: isCOD ? 'cod_commission_deduction' : 'commission_deduction',
              isCombinedCommission: true,
              adminCommissionAmount: t.type.includes('admin') ? (t.amount || 0) : (t.adminCommissionAmount || 0) || 0,
              vendorCommissionAmount: t.type.includes('vendor') ? (t.amount || 0) : (t.vendorCommissionAmount || 0) || 0,
            };
          } else {
            combinedCommission.amount = (combinedCommission.amount || 0) + (t.amount || 0);
            if (t.type.includes('admin')) {
              combinedCommission.adminCommissionAmount = (combinedCommission.adminCommissionAmount || 0) + (t.amount || 0);
            }
            if (t.type.includes('vendor')) {
              combinedCommission.vendorCommissionAmount = (combinedCommission.vendorCommissionAmount || 0) + (t.amount || 0);
            }
            if (new Date(t.date) > new Date(combinedCommission.date)) {
              combinedCommission.date = t.date;
            }
          }
        } else {
          const isEarning = t.type === 'online_ride_earning' || t.type === 'ride_earning' || t.type === 'earning';
          if (isEarning) {
            const isExtraCOD = t.bookingDetails?.extraChargesCollectedAsCOD === true;
            const extraKm = isExtraCOD ? 0 : (t.extraKmCharge || t.bookingDetails?.extraKmCharge || 0);
            const waiting = isExtraCOD ? 0 : (t.waitingCharge || t.bookingDetails?.waitingCharge || 0);
            const totalAmount = Math.abs(t.amount || 0);
            const baseFare = Math.max(0, totalAmount - extraKm - waiting);

            if (extraKm === 0 && waiting === 0) {
              processedTxs.push(t);
            } else {
              // 1. Base Fare earning row
              processedTxs.push({
                ...t,
                type: 'online_ride_earning_base',
                amount: baseFare,
                description: "Ride base fare credited",
              });

              // 2. Extra Km earning row
              if (extraKm > 0) {
                processedTxs.push({
                  ...t,
                  type: 'extra_km_earning',
                  amount: extraKm,
                  description: "Extra distance charges credited",
                });
              }

              // 3. Waiting Charge earning row
              if (waiting > 0) {
                processedTxs.push({
                  ...t,
                  type: 'waiting_charge_earning',
                  amount: waiting,
                  description: "Waiting time charges credited",
                });
              }
            }
          } else {
            processedTxs.push(t);
          }
        }
      }

      if (combinedCommission) {
        processedTxs.push(combinedCommission);
      }



      // Sort child transactions by custom priority order: Advance -> Remaining -> Commission -> GST
      const getPriority = (tx) => {
        if (tx.type === 'online_ride_earning' || tx.type === 'ride_earning' || tx.type === 'earning' || tx.type === 'online_ride_earning_base') {
          const isAdvance = tx.description && (tx.description.toLowerCase().includes('upfront') || tx.description.toLowerCase().includes('advance'));
          return isAdvance ? 1 : 2;
        }
        if (tx.type === 'extra_km_earning') return 2;
        if (tx.type === 'waiting_charge_earning') return 2;
        if (tx.type === 'cod_cash_collection') return 2;
        if (tx.type === 'commission_deduction' || tx.type === 'cod_commission_deduction') return 3;
        if (tx.type === 'gst_deduction' || tx.type === 'cod_gst_deduction') return 4;
        return 5;
      };

      processedTxs.sort((a, b) => getPriority(a) - getPriority(b));
      group.transactions = processedTxs;

      return group;
    });

    return [...nonBookingTransactions, ...groupedList];
  })();

  // Filter transactions based on current filters (use combined list)
  const filteredTransactions = combinedTransactions.filter(transaction => {
    // Type filter - use categories instead of exact types
    if (filters.type !== 'all') {
      if (transaction.type === 'grouped_ride') {
        // Match if any child transaction falls into this category
        const hasMatchingChild = transaction.transactions.some(
          child => getTransactionCategory(child.type) === filters.type
        );
        if (!hasMatchingChild) {
          return false;
        }
      } else {
        const category = getTransactionCategory(transaction.type);
        if (category !== filters.type) {
          return false;
        }
      }
    }

    // Date range filter
    if (filters.dateRange !== 'all') {
      const transactionDate = new Date(transaction.date);
      const daysAgo = parseInt(filters.dateRange);
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysAgo);
      
      if (transactionDate < cutoffDate) {
        return false;
      }
    }

    // Search filter
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      
      if (transaction.type === 'grouped_ride') {
        const description = (transaction.description || '').toLowerCase();
        const bookingId = getDisplayBookingId(transaction).toLowerCase();
        const bookingMatch = bookingId.includes(searchTerm);
        const childMatch = transaction.transactions.some(child => 
          (child.description || '').toLowerCase().includes(searchTerm) ||
          child.amount.toString().includes(searchTerm)
        );
        if (!bookingMatch && !description.includes(searchTerm) && !childMatch) {
          return false;
        }
      } else {
        const description = (transaction.description || '').toLowerCase();
        const amount = transaction.amount.toString();
        const bookingId = getDisplayBookingId(transaction).toLowerCase();
        const bookingMatch = bookingId.includes(searchTerm);
        
        if (!description.includes(searchTerm) && !amount.includes(searchTerm) && !bookingMatch) {
          return false;
        }
      }
    }

    return true;
  });

  // Sort transactions by date (newest first)
  const sortedTransactions = [...filteredTransactions].sort((a, b) => 
    new Date(b.date) - new Date(a.date)
  );

  // Pagination
  const totalItems = sortedTransactions.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedTransactions = sortedTransactions.slice(startIndex, startIndex + itemsPerPage);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  if (isLoading && !wallet) {
    return (
      <DriverLayout>
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary"></div>
          <div className="ml-4 text-gray-700 font-medium">Loading wallet history...</div>
        </div>
      </DriverLayout>
    );
  }

  return (
    <DriverLayout>
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/driver/dashboard')}
              className="mr-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
            >
              <FaArrowLeft className="text-gray-600" />
            </button>
            <div className="flex items-center">
              <FaWallet className="text-primary text-2xl mr-3" />
              <h1 className="text-2xl font-bold text-gray-800">Wallet History</h1>
            </div>
          </div>

          <button
            onClick={() => navigate('/driver/wallet/withdraw')}
            className="bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center"
          >
            <FaArrowUp className="mr-2" />
            Withdraw
          </button>
        </div>

        {/* Balance Summary */}
        <h2 className="text-xl font-semibold text-gray-800 mb-3">My Wallet</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">

          <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Current Balance</p>
                <p className="text-2xl font-bold">
                  {(() => {
                    const bal = Number(wallet?.balance ?? wallet?.withdrawableBalance ?? 0);
                    return formatCurrency(bal);
                  })()}
                </p>
              </div>
              <FaRupeeSign className="text-3xl text-green-200" />
            </div>
          </div>

          <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm">Total Commission Sent</p>
                <p className="text-2xl font-bold">
                  {(() => {
                    // Calculate commission paid from transactions if not available
                    let commissionPaid = wallet?.calculatedCommission || wallet?.totalCommissionPaid || 0;

                    if (commissionPaid === 0 && wallet?.transactions) {
                      commissionPaid = wallet.transactions
                        .filter(t =>
                          // Include only commission deductions, NOT GST
                          t.type === 'commission_deduction' ||
                          t.type === 'admin_commission_deduction' ||
                          t.type === 'vendor_commission_deduction' ||
                          t.type === 'commission_payment' ||
                          t.type === 'commission_cut' ||
                          t.type === 'cod_commission_deduction' ||
                          t.type === 'cod_admin_commission_deduction' ||
                          t.type === 'cod_vendor_commission_deduction' ||
                          t.type === 'cod_commission_settlement'
                          // Explicitly excluded: gst_deduction, cod_gst_deduction
                        )
                        .reduce((sum, t) => sum + (t.amount || 0), 0);
                    }

                    return formatCurrency(commissionPaid);
                  })()}
                </p>
              </div>
              <FaArrowDown className="text-3xl text-orange-200" />
            </div>
          </div>

          <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm">Total Withdrawals</p>
                <p className="text-2xl font-bold">{formatCurrency(wallet?.totalWithdrawals || 0)}</p>
              </div>
              <FaArrowDown className="text-3xl text-red-200" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Transaction Type Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Transaction Type
              </label>
              <select
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value="all">All Transactions</option>
                <option value="earnings">Ride Earnings</option>
                <option value="commission">Commission</option>
                <option value="withdrawal">Withdrawals</option>
                <option value="deposit">Deposits</option>
              </select>
            </div>

            {/* Date Range Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date Range
              </label>
              <select
                value={filters.dateRange}
                onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 3 months</option>
                <option value="365">Last year</option>
                <option value="all">All time</option>
              </select>
            </div>

            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search
              </label>
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  placeholder="Search transactions..."
                />
              </div>
            </div>

            {/* Export Button */}
            <div className="flex items-end">
              <button
                onClick={() => {
                  // Implement export functionality
                  toast.info('Export feature coming soon!');
                }}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-4 rounded-lg font-medium transition-colors flex items-center justify-center"
              >
                <FaDownload className="mr-2" />
                Export
              </button>
            </div>
          </div>
        </div>

        {/* Transactions List */}
        <div className="bg-white rounded-xl shadow-md">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-800">
                Transaction History ({totalItems} transactions)
              </h2>
              {filters.type !== 'all' || filters.search || filters.dateRange !== '30' ? (
                <button
                  onClick={() => {
                    setFilters({ type: 'all', dateRange: '30', search: '' });
                    setCurrentPage(1);
                  }}
                  className="text-primary hover:text-primary-dark text-sm font-medium"
                >
                  Clear Filters
                </button>
              ) : null}
            </div>
          </div>

          <div className="divide-y divide-gray-200">
            {paginatedTransactions.length > 0 ? (
              paginatedTransactions.map((transaction, index) => {
                const bookingKey = getDisplayBookingId(transaction);
                const isGrouped = transaction.type === 'grouped_ride';

                if (isGrouped) {
                  const isExpanded = !!expandedGroups[bookingKey];
                  return (
                    <div key={index} className="border-b border-gray-100 last:border-b-0">
                      {/* Main Group Header Row */}
                      <div
                        onClick={() => toggleGroup(bookingKey)}
                        className="p-6 hover:bg-gray-50/80 transition-colors cursor-pointer flex items-center justify-between"
                      >
                        <div className="flex items-center flex-1">
                          <div className="p-2.5 rounded-full bg-green-50 mr-4">
                            {getTransactionIcon(transaction.type)}
                          </div>
                          <div>
                            <div className="flex items-center space-x-2">
                              <p className="font-semibold text-gray-800">
                                {getTransactionTitle(transaction.type, transaction)}
                              </p>
                              <span className="text-[11px] bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full font-medium border border-blue-100">
                                {transaction.transactions.length} entries
                              </span>
                            </div>
                            <p className="text-sm text-gray-500 mt-0.5">
                              Ride summary for Booking ID: <span className="font-semibold text-gray-700">{bookingKey}</span>
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              {formatDate(transaction.date)}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center space-x-4">
                          <div className="text-right">
                            {(() => {
                              // Prefer server-computed netAmount from API; fall back to client calculation
                              let net;
                              if (transaction.netAmount !== undefined && transaction.netAmount !== null) {
                                net = transaction.netAmount;
                              } else if (transaction.breakdown?.netAmount !== undefined) {
                                net = transaction.breakdown.netAmount;
                              } else {
                                const DEBIT_TYPES = new Set([
                                  'admin_commission_deduction', 'vendor_commission_deduction',
                                  'gst_deduction', 'cod_gst_deduction',
                                  'cod_admin_commission_deduction', 'cod_vendor_commission_deduction',
                                  'commission_deduction', 'cod_commission_deduction',
                                  'commission_cut', 'cod_ride_deduction',
                                  'cod_commission_settlement', 'withdrawal', 'withdrawal_approved'
                                ]);
                                net = 0;
                                for (const t of transaction.transactions) {
                                  const abs = Math.abs(t.amount || 0);
                                  if (DEBIT_TYPES.has(t.type)) {
                                    net -= abs;
                                  } else {
                                    net += abs;
                                  }
                                }
                              }
                              const isPositive = net >= 0;
                              return (
                                <>
                                  <p className={`font-bold text-lg ${isPositive ? 'text-green-600' : 'text-red-500'}`}>
                                    {isPositive ? '+' : '-'}{formatCurrency(Math.abs(net))}
                                  </p>
                                  <p className="text-[11px] text-gray-400">Net Amount</p>
                                </>
                              );
                            })()}
                          </div>
                          <button
                            type="button"
                            className="p-1.5 rounded-full hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors"
                            aria-label={isExpanded ? "Collapse details" : "Expand details"}
                          >
                            {isExpanded ? (
                              <FaChevronUp className="text-sm" />
                            ) : (
                              <FaChevronDown className="text-sm" />
                            )}
                          </button>
                        </div>
                      </div>
                      {/* Collapsible Details Container */}
                      {isExpanded && (
                        <div className="bg-slate-50 border-t border-b border-gray-100 px-6 py-4 transition-all duration-300">
                          <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider pl-1 mb-3">
                            Itemized Ride Breakdown
                          </h4>
                          
                          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden divide-y divide-gray-100">
                            {transaction.transactions.map((child, cIdx) => {
                              const isDebit = child.type === 'commission_deduction' ||
                                child.type === 'admin_commission_deduction' ||
                                child.type === 'vendor_commission_deduction' ||
                                child.type === 'gst_deduction' ||
                                child.type === 'commission_cut' ||
                                child.type === 'cod_ride_deduction' ||
                                child.type === 'cod_commission_deduction' ||
                                child.type === 'cod_admin_commission_deduction' ||
                                child.type === 'cod_vendor_commission_deduction' ||
                                child.type === 'cod_gst_deduction' ||
                                child.type === 'cod_commission_settlement' ||
                                child.type === 'withdrawal' ||
                                child.type === 'withdrawal_approved';
                              // advance_paid and remaining_ride_earning are always credits
                              
                              return (
                                <div
                                  key={cIdx}
                                  className="p-4 flex items-center justify-between hover:bg-gray-50/50 transition-colors"
                                >
                                  <div className="flex items-center">
                                    <div className={`p-2 rounded-full mr-4 flex items-center justify-center ${
                                      isDebit ? 'bg-red-50 text-red-500' : 'bg-green-50 text-green-500'
                                    }`}>
                                      {getTransactionIcon(child.type)}
                                    </div>
                                    <div>
                                      <p className="text-sm font-semibold text-gray-800">
                                        {child.isCombinedCommission ? 'Commission Deducted' : getTransactionTitle(child.type, child)}
                                      </p>
                                      <p className="text-xs text-gray-500 mt-0.5">
                                        {formatTransactionDescription(child)}
                                      </p>
                                      {child.referenceId && (
                                        <p className="text-[11px] text-blue-600 font-medium mt-0.5">
                                          Reference ID: {child.referenceId}
                                        </p>
                                      )}
                                      {child.paymentProofImage && (
                                        <div className="flex items-center mt-1">
                                          <FaImage className="text-green-600 text-xs mr-1" />
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              window.open(child.paymentProofImage, '_blank');
                                            }}
                                            className="text-xs text-green-600 hover:text-green-700 font-medium"
                                            type="button"
                                          >
                                            View Payment Proof
                                          </button>
                                        </div>
                                      )}
                                      <p className="text-[10px] text-gray-400 mt-1">
                                        {formatDate(child.date)}
                                      </p>
                                    </div>
                                  </div>

                                  <div className="text-right min-w-[100px]">
                                    <p className={`font-bold text-sm ${getTransactionColor(child.type)}`}>
                                      {isDebit ? '-' : '+'}
                                      {formatCurrency(Math.abs(child.amount))}
                                    </p>
                                    {child.balanceAfter !== undefined && (
                                      <p className="text-[10px] text-gray-400 mt-0.5">
                                        Balance: {formatCurrency(child.balanceAfter)}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }

                // Ungrouped / Standard Transaction Layout (withdrawal, topup, etc.)
                return (
                  <div key={index} className="p-6 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="p-2.5 rounded-full bg-gray-100 mr-4">
                          {getTransactionIcon(transaction.type)}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-800">
                            {getTransactionTitle(transaction.type, transaction)}
                          </p>
                          <p className="text-sm text-gray-500 mt-0.5">
                            {formatTransactionDescription(transaction)}
                          </p>
                          {transaction.referenceId && (
                            <p className="text-xs text-blue-600 font-medium mt-0.5">
                              Reference ID: {transaction.referenceId}
                            </p>
                          )}
                          {transaction.paymentProofImage && (
                            <div className="flex items-center mt-1">
                              <FaImage className="text-green-600 text-xs mr-1" />
                              <button
                                onClick={() => window.open(transaction.paymentProofImage, '_blank')}
                                className="text-xs text-green-600 hover:text-green-700 font-medium"
                                type="button"
                              >
                                View Payment Proof
                              </button>
                            </div>
                          )}
                          <p className="text-xs text-gray-400 mt-1">
                            {formatDate(transaction.date)}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold text-lg ${getTransactionColor(transaction.type)}`}>
                          {transaction.type === 'commission_deduction' ||
                           transaction.type === 'admin_commission_deduction' ||
                           transaction.type === 'vendor_commission_deduction' ||
                           transaction.type === 'gst_deduction' ||
                           transaction.type === 'commission_cut' ||
                           transaction.type === 'cod_ride_deduction' ||
                           transaction.type === 'cod_commission_deduction' ||
                           transaction.type === 'cod_admin_commission_deduction' ||
                           transaction.type === 'cod_vendor_commission_deduction' ||
                           transaction.type === 'cod_gst_deduction' ||
                           transaction.type === 'cod_commission_settlement' ||
                           transaction.type === 'withdrawal' ||
                           transaction.type === 'withdrawal_approved' ? '-' : '+'}
                          {formatCurrency(Math.abs(transaction.amount))}
                        </p>
                        {transaction.balanceAfter !== undefined && (
                          <p className="text-sm text-gray-500 mt-0.5">
                            Balance: {formatCurrency(transaction.balanceAfter)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="p-12 text-center">
                <FaReceipt className="text-gray-300 text-4xl mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-500 mb-2">No transactions found</h3>
                <p className="text-gray-400">
                  {filters.type !== 'all' || filters.search || filters.dateRange !== '30'
                    ? 'Try adjusting your filters to see more transactions.'
                    : 'Start completing rides to see your transaction history here.'
                  }
                </p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="p-6 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-700">
                  Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, totalItems)} of{' '}
                  {totalItems} transactions
                </p>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNumber = Math.max(1, Math.min(currentPage - 2, totalPages - 4)) + i;
                    return (
                      <button
                        key={pageNumber}
                        onClick={() => setCurrentPage(pageNumber)}
                        className={`px-3 py-1 border rounded-md text-sm font-medium ${
                          pageNumber === currentPage
                            ? 'bg-primary text-white border-primary'
                            : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </DriverLayout>
  );
};

export default WalletHistory;
